/** GL — Audit Trail page (Vince-only private internal-notes aggregator). */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { PageHeader, Spinner } from '@/components/Common';
import { ListBulletsIcon, BuildingsIcon, CalendarBlankIcon, NoteIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AuditTrailPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/audit_trail');
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
        setData({ entries: [], count: 0 });
      }
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (data === null) return <Spinner label="Loading audit trail…" />;

  const filtered = q.trim()
    ? data.entries.filter((e) => {
        const blob = `${e.title} ${e.subtitle} ${e.internal_notes} ${e.engagement_business_name}`.toLowerCase();
        return blob.includes(q.trim().toLowerCase());
      })
    : data.entries;

  return (
    <div className="px-5 md:px-8 py-6">
      <PageHeader
        title="Audit Trail"
        subtitle="Every internal note you've recorded across engagements. Private — never sent to clients."
      />

      <div className="gl-card mb-4 flex items-center gap-3 flex-wrap" style={{ borderLeft: '4px solid var(--gl-gold)', background: '#fdf6e3' }}>
        <NoteIcon size={22} weight="fill" style={{ color: 'var(--gl-gold)' }} />
        <div className="text-sm flex-1" style={{ color: 'var(--gl-dark)' }}>
          <b>{data.count}</b> internal note{data.count === 1 ? '' : 's'} across your engagements.
          Use this as your chronological diary and VR&E reporting source.
        </div>
        <a
          href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/audit_trail/pdf?auth=${encodeURIComponent(localStorage.getItem('gl_token') || '')}`}
          target="_blank"
          rel="noreferrer"
          className="gl-btn gl-btn-primary !h-10 !px-4 !text-xs"
          data-testid="audit-trail-export-pdf-btn"
          title="Download audit trail as PDF"
        >
          <DownloadSimpleIcon size={16} weight="bold" /> Export PDF
        </a>
      </div>

      <div className="mb-4">
        <input
          type="search"
          className="gl-input"
          placeholder="Search notes, facility, citation…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="audit-trail-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="gl-card text-center py-10" style={{ color: 'var(--gl-muted)' }}>
          <ListBulletsIcon size={40} weight="bold" />
          <div className="mt-2 font-display font-extrabold uppercase text-lg" style={{ color: 'var(--gl-dark)' }}>
            {q ? 'No notes match that search' : 'No internal notes yet'}
          </div>
          <div className="text-sm mt-1">
            Add internal notes to findings and gap items as you work — they show up here.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => (
            <Link
              key={`${e.kind}-${e.id}`}
              to={`/engagements/${e.engagement_id}`}
              className="gl-card block hover:border-gray-400 transition-colors"
              style={{ borderLeft: `4px solid ${e.priority === 'P1' ? 'var(--gl-p1)' : e.priority === 'P2' ? 'var(--gl-p2)' : 'var(--gl-p3)'}` }}
              data-testid={`audit-trail-entry-${e.id}`}
            >
              <div className="flex items-baseline gap-2 flex-wrap mb-1">
                <span
                  className="text-xs uppercase font-bold px-1.5 py-0.5"
                  style={{
                    background: e.kind === 'finding' ? 'var(--gl-navy)' : 'var(--gl-dark)',
                    color: '#fff',
                    fontFamily: 'Inter',
                    letterSpacing: '0.08em',
                  }}
                >
                  {e.kind === 'finding' ? 'Finding' : 'Gap Item'}
                </span>
                {e.priority && (
                  <span
                    className="text-xs uppercase font-bold px-1.5 py-0.5"
                    style={{
                      background: e.priority === 'P1' ? 'var(--gl-p1)' : e.priority === 'P2' ? 'var(--gl-p2)' : 'var(--gl-p3)',
                      color: '#fff',
                      fontFamily: 'Inter',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {e.priority}
                  </span>
                )}
                <span
                  className="font-display font-extrabold uppercase tracking-wide"
                  style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.05em' }}
                >
                  {e.title}
                </span>
                <span className="ml-auto text-xs uppercase font-bold" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                  {e.updated_at ? new Date(e.updated_at).toLocaleDateString() : '—'}
                </span>
              </div>
              <div className="text-xs uppercase font-bold mb-2" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                <BuildingsIcon size={12} weight="bold" className="inline-block mr-1" />
                {e.engagement_business_name}
                {e.engagement_visit_date && (
                  <>
                    {' · '}
                    <CalendarBlankIcon size={12} weight="bold" className="inline-block mr-1" />
                    {e.engagement_visit_date}
                  </>
                )}
              </div>
              {e.subtitle && (
                <div className="text-xs mb-2" style={{ color: 'var(--gl-muted)' }}>
                  {e.subtitle}
                </div>
              )}
              <div
                className="text-sm p-3 border-l-2 whitespace-pre-wrap"
                style={{ borderColor: 'var(--gl-gold)', background: '#fffbeb', color: 'var(--gl-dark)' }}
              >
                {e.internal_notes}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
